import {copyFood, copyProduct} from "./CopyMenuDay";
import {Schema} from "../model/schema.model";
import {Food} from "../model/food.model";
import {dayFactory} from "../factory/day.factory";
import uuid from "uuid4";
import {planFactory} from "../factory/plan.factory";

export const multiPortionFoodsManager = {

    checkIsFoodMultiPortion(food) {
        if (!(food instanceof Food)) {
            throw new Error('Unsupported type');
        }

        if (food.isMultiPortionFood() === false) {
            throw new Error('Food must bu multi portion');
        }
    },

    checkIsSchema(schema) {
        if (!(schema instanceof Schema)) {
            throw new Error('Unsupported type');
        }
    },

    getUsedPortionsOfFood(food, schema) {
        this.checkIsSchema(schema);
        this.checkIsFoodMultiPortion(food);

        const usedPortions = schema.days.reduce((accumulator, day) => {
            return accumulator + day.plans.reduce((accumulator, plan) => {
                return accumulator + plan.foods.filter(planFood => planFood.externalId === food.externalId && planFood.id !== food.id)
                    .reduce((accumulator, planFood) => {
                        if (food.parentId === null) {
                            return accumulator + (planFood.parentId === food.id ? planFood.portion.amount : 0);
                        } else {
                            return accumulator + (planFood.parentId === food.parentId || food.parentId === planFood.id ? planFood.portion.amount : 0);
                        }
                    }, 0);
            }, 0);
        }, 0);

        return usedPortions + food.portion.amount;
    },

    getLeftPortionsOfFood(food, schema) {
        this.checkIsFoodMultiPortion(food);
        this.checkIsSchema(schema);

        return food.portions - this.getUsedPortionsOfFood(food, schema);
    },

    getFoodsWithLeftPortions(schema) {
        this.checkIsSchema(schema);

        let foodsWithLeftPortions = [];

        schema.days.forEach(day => {
            day.plans.forEach(plan => {
                const planFoodsWithLeftPortions = plan.foods.filter(planFood => {
                    return planFood.isMultiPortionFood() && planFood.parentId === null && this.getLeftPortionsOfFood(planFood, schema) > 0;
                }).map(planFood => {
                    let planFoodCopy = copyFood(planFood);
                    planFoodCopy.parentId = planFood.id;
                    planFoodCopy.portion.amount = this.getLeftPortionsOfFood(planFood, schema);
                    planFoodCopy.products = planFood.products;
                    return planFoodCopy;
                });

                foodsWithLeftPortions = foodsWithLeftPortions.concat(planFoodsWithLeftPortions);
            });
        });

        return foodsWithLeftPortions;
    },

    getPortionsOfFood(food, schema, sameInstances = false) {
        this.checkIsSchema(schema);
        this.checkIsFoodMultiPortion(food);

        let foodPortions = [];

        schema.days.forEach(day => {
            day.plans.forEach(plan => {
                const planPortions = plan.foods.filter(planFood => {
                    return planFood.isMultiPortionFood() &&
                        planFood.externalId === food.externalId &&
                        ((planFood.parentId === food.parentId && planFood.parentId !== null && food.parentId !== null) ||
                            planFood.id === food.parentId ||
                            planFood.parentId === food.id ||
                            planFood.id === food.id);
                }).map(planFood => {
                    return {
                        food: planFood,
                        day: day,
                        plan: plan,
                    };
                });
                if (sameInstances) {
                    planPortions.forEach(planPortion => {
                        foodPortions.push(planPortion);
                    });
                } else {
                    foodPortions = foodPortions.concat(planPortions);
                }
            });
        });

        return foodPortions;
    },

    getMultiPortionFoods(schema) {
        this.checkIsSchema(schema);

        let multiPortionFoods = [];

        schema.days.forEach(day => {
            day.plans.forEach(plan => {
                const planFoods = plan.foods.filter(planFood => {
                    return planFood.isMultiPortionFood() && planFood.parentId === null;
                });
                multiPortionFoods = multiPortionFoods.concat(planFoods);
            });
        });

        return multiPortionFoods;
    },

    getMultiPortionFoodColor(food, schema) {
        this.checkIsFoodMultiPortion(food);
        this.checkIsSchema(schema);

        const colorClasses = [
            'green--text',
            'blue--text',
            'orange--text',
            'purple--text',
            'indigo--text',
            'teal--text',
            'red--text',
            'yellow--text text--accent-4',
            'lime--text text--darken-1'
        ];

        const index = this.getMultiPortionFoods(schema).concat().sort((foodA, foodB) => {
            return foodB.date - foodA.date;
        }).findIndex(multiPortionFoodParent => {
            return multiPortionFoodParent.id === food.id || multiPortionFoodParent.id === food.parentId;
        });

        return colorClasses[index];
    },

    removeMultiPortionFood(foodToRemove, schema) {
        this.checkIsFoodMultiPortion(foodToRemove);
        this.checkIsSchema(schema);

        if (foodToRemove.parentId !== null) {
            return;
        }

        let relatedFoods = this.getPortionsOfFood(foodToRemove, schema, true).map(portion => {
            return portion.food;
        }).filter(food => {
            return food.id !== foodToRemove.id && food.parentId !== null;
        });

        if (relatedFoods.length === 0) {
            return;
        }

        let newParentFood = relatedFoods[0];
        newParentFood.parentId = null;

        if (relatedFoods.length <= 1) {
            return;
        }

        relatedFoods.forEach((relatedFood, index) => {
            if (index > 0) {
                relatedFood.parentId = newParentFood.id;
            }
        });
    },

    removeProductFromMultiPortionFood(food, schema, productId) {
        this.checkIsFoodMultiPortion(food);
        this.checkIsSchema(schema);

        this.getPortionsOfFood(food, schema, true).map(portion => {
            return portion.food;
        }).filter(portionFood => {
            return portionFood.id !== food.id;
        }).forEach(portionFood => {
            portionFood.removeProduct(productId);
        });
    },

    updateMultiPortionFood(food, schema) {
        this.checkIsFoodMultiPortion(food);
        this.checkIsSchema(schema);
        this.getPortionsOfFood(food, schema, true).map(portion => {
            return portion.food;
        }).filter(portionFood => {
            return portionFood.id !== food.id;
        }).forEach(portionFood => {
            portionFood.portions = food.portions;
            portionFood.name = food.name;
            portionFood.products = food.products;
            portionFood.recipe = food.recipe;
            portionFood.tags = food.tags;
            portionFood.diets = food.diets;
            portionFood.meals = food.meals;
            portionFood.equipments = food.equipments;
        });
    },

    copyMultiPortionFood(food, schema) {
        this.checkIsFoodMultiPortion(food);
        this.checkIsSchema(schema);
        const leftPortionsOfFood = this.getLeftPortionsOfFood(food, schema);
        let foodCopy = null;

        if (leftPortionsOfFood === 0) {
            foodCopy = copyFood(food);
            foodCopy.parentId = null;
            return foodCopy;
        } else {
            foodCopy = copyFood(food);
            foodCopy.parentId = food.parentId === null ? food.id : food.parentId;
            foodCopy.products = food.products;
            if (foodCopy.portion.amount > leftPortionsOfFood) {
                foodCopy.portion.amount = leftPortionsOfFood;
            }
        }

        return foodCopy;
    },

    copyDay(dayToCopy, date, schema) {
        this.checkIsSchema(schema);
        const day = dayFactory.create(
            uuid(),
            date,
            dayToCopy.comment
        );

        let parentFoods = [];

        if (dayToCopy.plans.length === 0) {
            return day;
        }

        dayToCopy.plans.map(planToCopy => {
            const plan = planFactory.create(
                uuid(),
                planToCopy.name,
                planToCopy.prescribedEnergy.amount,
                planToCopy.time,
                planToCopy.comment,
                [],
                [],
                date
            );

            if (planToCopy.products) {
                planToCopy.products
                    .map(p => copyProduct(p))
                    .map(p => plan.addProduct(p));
            }

            if (planToCopy.foods.length === 0) {
                return plan;
            }

            planToCopy.foods
                .sort((planFoodA, planFoodB) => {
                    if (planFoodA.parentId === null && planFoodB.parentId !== null) {
                        return -1;
                    } else if (planFoodA.parentId !== null && planFoodB.parentId === null) {
                        return 1;
                    } else {
                        return 0;
                    }
                })
                .map(foodToCopy => {
                    let newFood = copyFood(foodToCopy);

                    if (foodToCopy.isMultiPortionFood() && foodToCopy.parentId === null) {
                        parentFoods[foodToCopy.id] = {id: newFood.id, products: newFood.products};
                    }

                    if (foodToCopy.isMultiPortionFood() && foodToCopy.parentId) {
                        const parentFood = parentFoods[foodToCopy.parentId];

                        if (typeof parentFood === 'undefined') {
                            newFood.parentId = null;
                            parentFoods[foodToCopy.parentId] = {id: newFood.id, products: newFood.products};
                        } else {
                            newFood.parentId = parentFood.id;
                            newFood.products = parentFood.products;
                        }
                    }

                    return newFood;
                })
                .map(f => plan.addFood(f));

            return plan;
        }).map(p => day.addPlan(p));

        return day;
    },

    addMultiPortionFoodToPlan(food, schema, plan) {
        this.checkIsFoodMultiPortion(food);
        this.checkIsSchema(schema);
        let planFood = plan.foods.find(planFood => {
            return planFood.externalId === food.externalId && (planFood.parentId === food.parentId || planFood.id === food.parentId);
        });

        if (typeof planFood === 'undefined') {
            plan.addFood(food);
        } else {
            if (planFood.portion.amount + food.portion.amount > food.portions) {
                const portionAmountForCurrentPlanFood = food.portions - planFood.portion.amount;
                planFood.portion.amount = planFood.portion.amount + portionAmountForCurrentPlanFood;
                food.portion.amount = food.portion.amount - portionAmountForCurrentPlanFood;
                plan.addFood(food);
            } else {
                planFood.portion.amount = planFood.portion.amount + food.portion.amount;
            }
        }
    },
};
